import React, { useEffect, useMemo, useState } from "react";
import { Loader2, ArrowUp, ArrowDown, Waves, Moon } from "lucide-react";

const LAT = 11.2528, LON = -85.8703;
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const fmtTime = (iso) => {
  const [h0, m] = iso.split("T")[1].split(":").map(Number);
  const ap = h0 >= 12 ? "PM" : "AM";
  const h = h0 % 12 === 0 ? 12 : h0 % 12;
  return `${h}:${String(m).padStart(2, "0")} ${ap}`;
};

const dowOf = (dateStr) => DOW[new Date(dateStr + "T12:00:00").getDay()];

// Local maxima (high) / minima (low) from the hourly tide series.
const findExtrema = (times, heights) => {
  const ev = [];
  for (let i = 1; i < heights.length - 1; i++) {
    const p = heights[i - 1], c = heights[i], n = heights[i + 1];
    if (c > p && c >= n) ev.push({ type: "high", time: times[i], h: c });
    else if (c < p && c <= n) ev.push({ type: "low", time: times[i], h: c });
  }
  return ev;
};

const nowInManagua = () => {
  const s = new Date().toLocaleString("en-US", { timeZone: "America/Managua" });
  return new Date(s);
};

const TideTracker = ({ compact = false }) => {
  const [state, setState] = useState({ loading: true, error: false, times: [], heights: [] });
  const [tab, setTab] = useState("today");

  useEffect(() => {
    const url = `https://marine-api.open-meteo.com/v1/marine?latitude=${LAT}&longitude=${LON}&hourly=sea_level_height_msl&timezone=America%2FManagua&forecast_days=7`;
    fetch(url)
      .then((r) => r.json())
      .then((d) => {
        const times = d.hourly?.time || [];
        const heights = d.hourly?.sea_level_height_msl || [];
        if (!times.length) throw new Error("no data");
        setState({ loading: false, error: false, times, heights });
      })
      .catch(() => setState({ loading: false, error: true, times: [], heights: [] }));
  }, []);

  const model = useMemo(() => {
    if (!state.times.length) return null;
    const { times, heights } = state;
    const events = findExtrema(times, heights);

    // group event days
    const byDay = {};
    events.forEach((e) => {
      const d = e.time.split("T")[0];
      (byDay[d] = byDay[d] || []).push(e);
    });
    const dayKeys = Object.keys(byDay).sort();
    const todayKey = dayKeys[0];

    // today's hourly slice for the sparkline
    const todayIdx = [];
    times.forEach((t, i) => { if (t.startsWith(todayKey)) todayIdx.push(i); });
    const todayH = todayIdx.map((i) => heights[i]);
    const todayT = todayIdx.map((i) => times[i]);

    // "now" pointer + rising/falling
    const now = nowInManagua();
    let nowI = 0, best = Infinity;
    times.forEach((t, i) => {
      const diff = Math.abs(new Date(t + ":00").getTime() - now.getTime());
      if (diff < best) { best = diff; nowI = i; }
    });
    const curH = heights[nowI];
    const rising = nowI > 0 ? heights[nowI] >= heights[nowI - 1] : true;
    const nowFrac = todayT.length ? Math.max(0, Math.min(1, now.getHours() / 23)) : 0;

    return { byDay, dayKeys, todayKey, todayT, todayH, curH, rising, nowFrac, todayEvents: byDay[todayKey] || [] };
  }, [state]);

  if (state.loading) return (
    <div className="rounded-2xl border border-white/10 bg-[#0e2740] p-10 flex items-center justify-center" data-testid="tide-loading"><Loader2 className="h-7 w-7 animate-spin text-[#f5a524]" /></div>
  );
  if (state.error || !model) return (
    <div className="rounded-2xl border border-white/10 bg-[#0e2740] p-8 text-center text-slate-400" data-testid="tide-error">Tide data is temporarily unavailable. Please try again shortly.</div>
  );

  // sparkline geometry
  const W = 640, H = 90, PAD = 6;
  const hs = model.todayH;
  const min = Math.min(...hs), max = Math.max(...hs);
  const rng = max - min || 1;
  const pts = hs.map((v, i) => {
    const x = PAD + (i / (hs.length - 1 || 1)) * (W - PAD * 2);
    const y = PAD + (1 - (v - min) / rng) * (H - PAD * 2);
    return [x, y];
  });
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = `${line} L${(W - PAD).toFixed(1)},${H - PAD} L${PAD},${H - PAD} Z`;
  const nowX = PAD + model.nowFrac * (W - PAD * 2);

  const EventChip = ({ e }) => (
    <div className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${e.type === "high" ? "border-[#f5a524]/30 bg-[#f5a524]/10" : "border-[#5cb3e6]/30 bg-[#5cb3e6]/10"}`}>
      {e.type === "high"
        ? <ArrowUp className="h-4 w-4 text-[#f5a524]" />
        : <ArrowDown className="h-4 w-4 text-[#5cb3e6]" />}
      <div className="leading-tight">
        <p className={`text-[10px] uppercase tracking-wide font-heading ${e.type === "high" ? "text-[#f5a524]" : "text-[#5cb3e6]"}`}>{e.type === "high" ? "High Tide" : "Low Tide"}</p>
        <p className="text-sm text-white font-semibold">{fmtTime(e.time)} <span className="text-slate-400 font-normal">· {e.h.toFixed(1)}m</span></p>
      </div>
    </div>
  );

  const weekDays = model.dayKeys.slice(0, 7);

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0e2740] overflow-hidden" data-testid="tide-tracker">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-[#071726] px-6 py-4">
        <div className="flex items-center gap-3">
          <Moon className="h-8 w-8 text-[#5cb3e6]" />
          <div>
            <p className="font-display text-2xl text-white leading-none">Tide Tracker</p>
            <p className="text-xs text-slate-400 mt-1">San Juan del Sur · {model.curH.toFixed(1)}m · {model.rising ? "Rising" : "Falling"}</p>
          </div>
        </div>
        <div className="flex rounded-full bg-white/5 p-1 text-xs">
          <button onClick={() => setTab("today")} data-testid="tide-tab-today" className={`px-4 py-1.5 rounded-full transition ${tab === "today" ? "bg-[#f5a524] text-[#0a1f33] font-semibold" : "text-slate-300"}`}>Today</button>
          <button onClick={() => setTab("week")} data-testid="tide-tab-week" className={`px-4 py-1.5 rounded-full transition ${tab === "week" ? "bg-[#f5a524] text-[#0a1f33] font-semibold" : "text-slate-300"}`}>This Week</button>
        </div>
      </div>

      {tab === "today" ? (
        <div className="p-6" data-testid="tide-today">
          <div className="relative">
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-24" preserveAspectRatio="none">
              <defs>
                <linearGradient id="tideFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#5cb3e6" stopOpacity="0.45" />
                  <stop offset="100%" stopColor="#5cb3e6" stopOpacity="0.02" />
                </linearGradient>
              </defs>
              <path d={area} fill="url(#tideFill)" />
              <path d={line} fill="none" stroke="#5cb3e6" strokeWidth="2" />
              <line x1={nowX} y1={PAD} x2={nowX} y2={H - PAD} stroke="#f5a524" strokeWidth="1.5" strokeDasharray="3 3" />
            </svg>
            <span className="absolute -top-1 text-[10px] text-[#f5a524] font-heading" style={{ left: `${model.nowFrac * 100}%`, transform: "translateX(-50%)" }}>now</span>
          </div>
          <div className={`grid ${compact ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-4"} gap-3 mt-5`}>
            {model.todayEvents.length
              ? model.todayEvents.map((e, i) => <EventChip key={i} e={e} />)
              : <p className="text-slate-400 text-sm col-span-full">No tide turns detected for today.</p>}
          </div>
        </div>
      ) : (
        <div className="divide-y divide-white/5" data-testid="tide-week">
          {weekDays.map((d) => {
            const evs = model.byDay[d] || [];
            const isToday = d === model.todayKey;
            return (
              <div key={d} className={`flex items-center gap-4 px-6 py-3 ${isToday ? "bg-[#f5a524]/5" : ""}`}>
                <div className="w-14 shrink-0">
                  <p className={`font-heading text-sm ${isToday ? "text-[#f5a524]" : "text-white"}`}>{isToday ? "Today" : dowOf(d)}</p>
                  <p className="text-[10px] text-slate-500">{d.slice(5)}</p>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                  {evs.length ? evs.map((e, i) => (
                    <span key={i} className="flex items-center gap-1.5">
                      {e.type === "high"
                        ? <ArrowUp className="h-3.5 w-3.5 text-[#f5a524]" />
                        : <ArrowDown className="h-3.5 w-3.5 text-[#5cb3e6]" />}
                      <span className="text-slate-200">{fmtTime(e.time)}</span>
                      <span className="text-slate-500">{e.h.toFixed(1)}m</span>
                    </span>
                  )) : <span className="text-slate-500">—</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-2 px-6 py-3 border-t border-white/5">
        <Waves className="h-3.5 w-3.5 text-[#0e9488]" />
        <p className="text-[11px] text-slate-500">Predicted tide heights · updated daily · Open-Meteo. Plan bites around the moving water.</p>
      </div>
    </div>
  );
};

export default TideTracker;
